import { FramerButton } from "@/components/shared";
import { InputGroup } from "@/types";
import { ChangeEvent, FormEvent, useState } from "react";
import { passwordInputFields } from "./inputfields";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getPasswordStrength } from "@/utils/form";
import { PasswordRequirementsDisplay } from "./PasswordRequirementsDisplay";

type Props = {
  handleOnChange: (_event: ChangeEvent<HTMLInputElement>) => void;
  handleFormSubmit: (_event: FormEvent<Element>) => void;
  hasErrors: boolean;
  loading: boolean;
  confirmPasswordError: boolean | null;
}

function ActivateAccountComponent({ handleOnChange, handleFormSubmit, hasErrors, loading, confirmPasswordError }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Local state for password validation
  const [formValues, setFormValues] = useState<{[key: string]: string}>({
    newPassword: "",
    confirmNewPassword: ""
  });
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string | null}>({
    newPassword: null,
    confirmNewPassword: null
  });

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Update local form values
    const updatedValues = { ...formValues, [name]: value };
    setFormValues(updatedValues);

    // Validate password requirements for newPassword field
    if (name === "newPassword") {
      const passwordStrength = getPasswordStrength(value);
      const isValid = passwordStrength.requiredLength && passwordStrength.hasNumberOrSpecial && passwordStrength.hasUppercase;
      setFieldErrors(prev => ({
        ...prev,
        newPassword: !isValid && value.length > 0 ? "Password must meet all requirements" : null
      }));
    }

    // Validate password matching for confirmNewPassword field
    if (name === "confirmNewPassword") {
      const passwordsMatch = value === updatedValues.newPassword;
      setFieldErrors(prev => ({
        ...prev,
        confirmNewPassword: !passwordsMatch && value.length > 0 ? t("toasts:passwdsDoNotMatch", "Passwords do not match") : null
      }));
    }

    // Also validate confirm password when main password changes
    if (name === "newPassword" && updatedValues.confirmNewPassword) {
      const passwordsMatch = value === updatedValues.confirmNewPassword;
      setFieldErrors(prev => ({
        ...prev,
        confirmNewPassword: !passwordsMatch ? t("toasts:passwdsDoNotMatch", "Passwords do not match") : null
      }));
    }

    // Call parent handler
    handleOnChange(event);
  };

  const renderButtonContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center">
          <div className="w-4 h-4 border-2 border-gray-400 border-t-white mr-2 rounded-full animate-spin" />
          Setting Password
        </div>
      );
    }
    return <>Set Password</>;
  };

  return (
    <div className="mb-[50px] md:mb-0 mt-4 md:mt-8">
      {/* Header wrapper */}
      <div className="mb-4">
        <p className="text-xl md:text-2xl font-semibold mb-1 dark:text-white">Account Activation</p>
        <p className="text-sm md:text-base dark:text-white">Please set a new password to activate your account.</p>
      </div>

      <form
        className="flex flex-col gap-2"
        onSubmit={handleFormSubmit}
        id="loginForm"
      >
        {
          passwordInputFields.map(({
            className,
            required,
            placeholder,
            type,
            name,
            title,
            errorMessage,
            doNotUseRegex,
            autoFocus,
            usePasswordRequirements
          }: InputGroup) => {
            const hasError = fieldErrors[name] || (name === "confirmNewPassword" && confirmPasswordError);
            const shouldShowRequirements = usePasswordRequirements && name === "newPassword";

            return (
              <div key={`input-wrapper-${name}`}>
                <Label htmlFor={name} className="dark:text-white">{title}</Label>
                <Input
                  key={`login-input-${name}`}
                  className={`${className} bg-transparent ${hasError ? "border-red-500 focus:border-red-500" : ""}`}
                  required={required}
                  onChange={handleInputChange}
                  placeholder={placeholder}
                  type={type}
                  name={name}
                  autoFocus={autoFocus}
                  value={formValues[name] || ""}
                />
                {hasError && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {fieldErrors[name] || errorMessage}
                  </p>
                )}
                {shouldShowRequirements && !doNotUseRegex && (
                  <PasswordRequirementsDisplay password={formValues[name] || ""} />
                )}
              </div>
            );
          })
        }
        <FramerButton
          className={`
            rounded-md bg-mforange text-white hover:bg-mforange/80 w-full
            ${loading ? "bg-mforange/50 cursor-wait" : ""}
            ${hasErrors ? "cursor-not-allowed bg-gray-400" : ""}
          `}
          type={hasErrors ? "button" : "submit"}
          disabled={hasErrors}
        >
          {renderButtonContent()}
        </FramerButton>

        {/* Back to login */}
        <p className="mt-4 text-sm md:text-base">
          <span onClick={() => {navigate("/login");}} className="text-blue-400 cursor-pointer">Back to Login</span>
        </p>
      </form>
    </div>
  );
}

export default ActivateAccountComponent;
